/**
 * Configures Box folders through the shared Vault connector-feed lifecycle.
 *
 * Box keeps user-scoped OAuth consent, while folder identity, initial transfer,
 * cadence, and disconnect use the same durable feed contract as other Vault
 * connectors. The Fable shell is presentation only; provider state stays real.
 */
import { useEffect, useState } from 'react'
import { Folder, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import type { NodeApiClient } from '@/app-shell/api/node-api-client'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui'
import { ConnectorDialogSection } from '../../components/ConnectorDialogSection'
import { connectorScheduleLabels } from '../../components/ConnectorScheduleSelect'
import { updateConnectorFeedSchedule } from '../../api'
import type {
  ConnectorFeedFor,
  ConnectorInitialSync,
  ConnectorSchedule,
  ConnectorSelectionPreview,
} from '../../types'
import { sameConnectorSelection } from '../../types'
import { createBoxConnectorFeed, previewBoxConnectorFeed } from '../services/api'
import type { BoxEntry } from '../services/source-api'
import type { BoxOAuthConfig } from '../types'
import { BoxConnection } from './BoxConnection'
import { BoxContentScope } from './BoxContentScope'
import { BoxSyncOptions } from './BoxSyncOptions'

export function BoxFeedDialog({
  api,
  orgId,
  vaultId,
  feed,
  token,
  config,
  loadingConfig,
  open,
  onOpenChange,
  onConfigured,
  onSyncStarted,
  onConfigChanged,
  onConnect,
  onDisconnect,
}: {
  api: NodeApiClient
  orgId: string
  vaultId: string
  feed?: ConnectorFeedFor<'box'> | null
  token: string | null
  config?: BoxOAuthConfig
  loadingConfig: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfigured: () => Promise<void>
  onSyncStarted: () => void
  onConfigChanged: () => Promise<void>
  onConnect: () => void
  onDisconnect: () => Promise<boolean>
}) {
  const [selected, setSelected] = useState<BoxEntry[]>([])
  const [initialSync, setInitialSync] = useState<ConnectorInitialSync>('all')
  const [schedule, setSchedule] = useState<ConnectorSchedule>('manual')
  const [saving, setSaving] = useState(false)
  const [preview, setPreview] = useState<ConnectorSelectionPreview>()
  const [sections, setSections] = useState({
    connection: false,
    scope: true,
    sync: false,
  })

  useEffect(() => {
    if (!open) return
    setSelected((feed?.box?.folders ?? []).map(folder => ({
      ...folder,
      type: 'folder',
    })))
    setInitialSync('all')
    setSchedule(feed?.schedule ?? 'manual')
    setPreview(undefined)
    setSections({ connection: !feed && !token, scope: true, sync: false })
  }, [feed, open, token])

  useEffect(() => {
    setPreview(undefined)
  }, [initialSync, schedule, selected])

  const folders = selected
  const configured = Boolean(config?.configured)
  const editing = Boolean(feed)
  const canConnect = configured && Boolean(config?.oauthStorageConfigured)
  const oneTime = schedule === 'manual'
  const scheduleLabel = oneTime
    ? 'One-time import · nothing scheduled'
    : `${connectorScheduleLabels[schedule]} · new and changed files only`
  const setSection = (section: keyof typeof sections, sectionOpen: boolean) => {
    setSections(current => ({ ...current, [section]: sectionOpen }))
  }
  const submit = async () => {
    if (!folders.length || (!feed && !token)) return
    setSaving(true)
    try {
      if (feed) {
        // cm-coding: A Box feed's checkpoints and imported documents identify
        // the folder selected when the feed was created. Configuration may
        // change its schedule, but changing the folder in place would mix two
        // folders under one import history. To target another folder, the user
        // must disconnect this feed and create a new one.
        await updateConnectorFeedSchedule(
          api,
          orgId,
          vaultId,
          feed.feedId,
          schedule,
        )
        toast.success('Box source updated')
        onOpenChange(false)
        void onConfigured().catch(() => {
          toast.error('Box source was saved, but connected views could not refresh')
        })
        return
      }
      const request = {
        box: {
          folders: folders.map(({ id, name }) => ({ id, name })),
        },
        initialSync,
        schedule,
        sourceDisplayFieldKeys: [],
      }
      const nextPreview = await previewBoxConnectorFeed(
        api,
        orgId,
        vaultId,
        request,
      )
      const selectionConfirmed = sameConnectorSelection(preview, nextPreview)
      setPreview(nextPreview)
      if (!selectionConfirmed) return
      await createBoxConnectorFeed(api, orgId, vaultId, {
        ...request,
      })
      toast.info('Box source connected. Sync started.')
      onOpenChange(false)
      onSyncStarted()
      void onConfigured().catch(() => {
        toast.error('Sync started, but Vault refresh failed')
      })
    } catch (error: any) {
      toast.error(error?.response?.data?.detail ?? error?.message ?? 'Box folder sync failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={next => !saving && onOpenChange(next)}>
      <DialogContent data-testid="box-source-dialog" className="flex max-h-[calc(100vh-2rem)] max-w-[calc(100vw-2rem)] flex-col gap-0 overflow-hidden rounded-[14px] p-0 shadow-[0_24px_60px_rgba(15,23,42,0.18)] sm:max-w-[720px]">
        <DialogHeader className="shrink-0 border-b border-slate-100 px-6 py-4 pr-12 text-left">
          <div className="flex items-center gap-3">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-[9px] bg-slate-50 text-slate-700">
              <Folder className="h-[18px] w-[18px]" />
            </span>
            <div>
              <DialogTitle className="text-[16.5px] font-semibold leading-5 text-slate-950">
                Configure Box
              </DialogTitle>
              <DialogDescription className="mt-0.5 text-[13px] leading-4 text-slate-500">
                Box account, source folders, and sync behavior.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <ConnectorDialogSection
            title="Connection"
            summary={token
              ? 'Box OAuth · connected'
              : editing
                ? 'Box OAuth · reconnect when needed'
                : 'Box OAuth · connection required'}
            open={sections.connection}
            onOpenChange={sectionOpen => setSection('connection', sectionOpen)}
          >
            <BoxConnection
              api={api}
              orgId={orgId}
              vaultId={vaultId}
              token={token}
              config={config}
              loading={loadingConfig}
              onConfigChanged={onConfigChanged}
              onConnect={onConnect}
              onDisconnect={onDisconnect}
            />
          </ConnectorDialogSection>

          {(token || feed) && (
            <ConnectorDialogSection
              title="Content & scope"
              summary={folders.length
                ? `${folders.length} folder${folders.length === 1 ? '' : 's'} selected`
                : 'Choose Box folders'}
              open={sections.scope}
              onOpenChange={sectionOpen => setSection('scope', sectionOpen)}
            >
              <BoxContentScope
                token={token}
                selected={selected}
                onSelectedChange={setSelected}
                initialSync={initialSync}
                onInitialSyncChange={setInitialSync}
                schedule={schedule}
                onScheduleChange={setSchedule}
                folderLocked={editing}
              />
            </ConnectorDialogSection>
          )}

          <ConnectorDialogSection
            title="Sync schedule"
            summary={scheduleLabel}
            open={sections.sync}
            onOpenChange={sectionOpen => setSection('sync', sectionOpen)}
          >
            <BoxSyncOptions schedule={schedule} onScheduleChange={setSchedule} />
          </ConnectorDialogSection>
        </div>

        <div className="shrink-0 border-t border-slate-100 bg-slate-50/80">
          {preview && (
            <div className="border-b border-slate-100 px-6 py-3 text-xs leading-5 text-slate-600">
                {folders.length === 1 ? 'This Box folder contains' : 'These Box folders contain'}{' '}
              <strong className="font-semibold text-slate-800">
                {preview.itemCount.toLocaleString()} file{preview.itemCount === 1 ? '' : 's'}
              </strong>
              . DocReview will connect these folders and synchronize the selected history.
            </div>
          )}

          <DialogFooter className="px-6 py-4 sm:items-center sm:justify-end">
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={saving}
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                data-testid="box-source-save"
                size="sm"
                className="bg-slate-900 hover:bg-slate-800"
                disabled={saving || !folders.length || (!editing && (!canConnect || !token))}
                onClick={() => void submit()}
              >
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {saving
                  ? editing ? 'Saving…' : 'Connecting…'
                  : editing
                    ? 'Save changes'
                    : !preview
                    ? 'Check selection'
                    : 'Connect source'}
              </Button>
            </div>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}
